import type { Report, AiIntelReport } from '../data/store'

// ============================================================================
// DENS CAKRA — Simulated AI Intelligence Generator
// This is a DETERMINISTIC, on-device simulation of an AI analysis engine.
// It derives a structured, editable intelligence report from the raw field
// information already on the report record. No external LLM is called; the
// output is templated from the source inputs so the prototype can demonstrate
// "AI-assisted intelligence production with mandatory human-analyst validation."
// The generated report is always editable and never published automatically.
// ============================================================================

function nowParts() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    tanggal: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

function classificationFor(urgency?: string): string {
  switch (urgency) {
    case 'Kritis':
      return 'SANGAT RAHASIA'
    case 'Tinggi':
      return 'RAHASIA'
    case 'Sedang':
      return 'TERBATAS'
    default:
      return 'BIASA'
  }
}

/**
 * Build a structured intelligence report from a report's raw field inputs.
 * Pure function — callers persist the result via store.saveAiReport.
 */
export function generateAiReport(report: Report): AiIntelReport {
  const { tanggal, time } = nowParts()
  const region = report.region || report.provinsi || 'wilayah tidak diketahui'
  const category = report.category || 'umum'
  const narrative =
    report.ringkasan ||
    report.description ||
    report.kronologi ||
    'Informasi lapangan terbatas.'
  const actors =
    report.pihakTerkait || 'Belum teridentifikasi dari informasi awal.'

  const keyFindings = [
    `Sumber melaporkan aktivitas berkategori ${category} di ${region}.`,
    `Tingkat urgensi awal dinilai ${report.urgency || 'Sedang'} berdasarkan informasi lapangan.`,
    report.pihakTerkait
      ? `Teridentifikasi pihak terkait: ${report.pihakTerkait}.`
      : 'Pihak terkait masih dalam proses identifikasi.',
    report.attachments?.length
      ? `Tersedia ${report.attachments.length} lampiran pendukung untuk verifikasi.`
      : 'Belum ada lampiran pendukung yang diterima.',
  ]

  const earlyWarning = [
    `Potensi eskalasi situasi ${category} di ${region} bila tidak ditindaklanjuti.`,
    `Pantau indikator pergerakan dan komunikasi pihak terkait dalam 24-48 jam.`,
  ]

  return {
    classification: classificationFor(report.urgency),
    nomor: `INTEL/${report.id}/${new Date().getFullYear()}`,
    tanggal,
    subject: report.title || `Analisis Intelijen ${category} — ${region}`,
    facts:
      `Berdasarkan informasi yang disampaikan sumber (${report.sender || 'sumber lapangan'}) ` +
      `pada ${report.time || tanggal} di ${region}:\n\n${narrative}` +
      (report.kronologi ? `\n\nKronologi: ${report.kronologi}` : ''),
    situationSummary:
      `Informasi lapangan mengindikasikan dinamika ${category} di ${region}. ` +
      `Penilaian awal menempatkan situasi pada tingkat urgensi ${report.urgency || 'Sedang'} ` +
      `dan memerlukan verifikasi serta analisis lanjutan oleh analis.`,
    keyFindings,
    mainActors: actors,
    riskAnalysis:
      `Risiko utama terkait kategori ${category} mencakup potensi gangguan stabilitas lokal di ${region}. ` +
      `Tingkat keyakinan analisis: SEDANG (memerlukan korelasi dengan laporan lain).`,
    potentialImpact:
      report.potensiDampak ||
      `Bila tidak ditangani, situasi berpotensi memengaruhi keamanan dan ketertiban di ${region} ` +
        `serta dapat meluas ke wilayah sekitarnya.`,
    earlyWarning,
    recommendations:
      report.rekomendasiAwal ||
      `1. Verifikasi keaslian sumber dan lampiran.\n` +
        `2. Lakukan pemantauan lanjutan di ${region}.\n` +
        `3. Koordinasikan dengan unit terkait untuk kesiapan respons.`,
    assessment:
      `Penilaian analis: informasi dinilai relevan dan layak ditindaklanjuti. ` +
      `Diperlukan validasi tambahan sebelum eskalasi ke tingkat nasional.`,
    authentication:
      `Dihasilkan oleh DENS CAKRA AI Analysis Engine — DRAF (belum tervalidasi). ` +
      `Wajib divalidasi dan disetujui analis. ${tanggal} ${time}.`,
    generatedAt: `${tanggal} ${time}`,
  }
}
